import { emptyEndingBlock } from 'app/components/pages/Edito/Event/Detail/EditoEventDetail';
import type { Tag } from 'app/redux/models/Tag/Tag';
import { config } from 'app/redux/store';
import type {
  EditoActionFooterType,
  EditoActionType,
  FilterCategory,
  IEditoEventForm,
  IEditoEventRequest,
  IEditoEventResponse,
  IEditoEventSectionBody,
  IEditoEventSectionFooter,
  IEditoEventSectionForm,
  IEditoEventSectionRequest,
  IEditoEventSectionResponse,
  SearchFilters,
  TagCriteria,
} from 'app/typings/edito';
import type { SearchFiltersParams } from 'app/typings/filters';
import { formatToLocalDateTime } from 'app/utils/dates';
import {
  buildDaytimePackValueFromSection,
  filterCategoryByCriteria,
} from 'app/utils/edito';
import { DEFAULT_PAX, parseRange } from 'app/utils/filters/filters';
import {
  decodeSearchString,
  encodeToSearchString,
} from 'app/utils/filters/queryParams';
import { placesService } from 'app/utils/googlePlace/services';
import type { PlaceResult } from 'app/utils/googlePlace/typings';
import { parsePackSelectionValue } from 'app/utils/packSelection';
import { isDefined } from 'app/utils/typing';

const buildQueryParams = (searchFilters: SearchFilters) => {
  const hotelServices = searchFilters.accessibility
    ?.map((accessibility) => parseInt(accessibility, 10))
    .filter((accessibility) => !Number.isNaN(accessibility));

  const searchParams: SearchFiltersParams = {
    coords: searchFilters.coords,
    adults: searchFilters.pax?.adults,
    babies: searchFilters.pax?.babies,
    children: searchFilters.pax?.children,
    dates: searchFilters.dates,
    distances: searchFilters.distances
      ? searchFilters.distances.map((distance) => parseRange(distance))
      : undefined,
    prices: searchFilters.prices
      ? searchFilters.prices.map((price) => parseRange(price))
      : undefined,
    hotelServices,
    pmr: searchFilters.accessibility?.includes('pmr'),
    nightCount: searchFilters.nightCount,
    sort: searchFilters.sort,
    around: searchFilters.around,
    tagIds: [
      ...(searchFilters.experience ?? []),
      ...(searchFilters.room ?? []),
      ...(searchFilters.style ?? []),
      ...(searchFilters.situation ?? []),
    ],
  };

  return encodeToSearchString(searchParams);
};

const buildAppLinkFromSearchFilters = (searchFilters: SearchFilters) => {
  const appLinkQueryParams = buildQueryParams(searchFilters);
  const locality = searchFilters.location?.toLowerCase();

  return `https://www.staycation.co/fr/search${
    locality ? `/${locality}` : ''
  }?${appLinkQueryParams}`;
};

const buildWebLinkFromSearchFilters = (searchFilters: SearchFilters) => {
  const webLinkQueryParams = buildQueryParams({
    ...searchFilters,
    around: searchFilters.coords ? undefined : true,
  });

  const locality = searchFilters.location?.toLowerCase();

  return `${config.appUrl}/search${
    locality ? `/${locality}` : ''
  }?${webLinkQueryParams}`;
};

const tagIdsByCriteria = (tags: Tag[], criteria: TagCriteria) => {
  return tags
    .filter(
      (tag) =>
        tag.filterCategory &&
        filterCategoryByCriteria[criteria].includes(
          tag.filterCategory as FilterCategory
        )
    )
    .map((tag) => tag.id);
};

const selectedCriteriaTagIds = (
  tags: Tag[],
  criteria: TagCriteria,
  tagIds?: number[]
) => {
  if (!tagIds) {
    return undefined;
  }

  const selectedTagIds = tagIds.filter((tagId) => {
    return tagIdsByCriteria(tags, criteria).includes(tagId);
  });

  return selectedTagIds.length ? selectedTagIds : undefined;
};

const buildSearchFiltersFromWebLink = (
  webLink: string,
  tags: Tag[]
): SearchFilters | undefined => {
  const searchFiltersParams = decodeSearchString(webLink);

  if (searchFiltersParams) {
    const experienceType =
      searchFiltersParams.nightCount === 0 ? 'moment' : 'staycation';

    const pax =
      searchFiltersParams.babies ||
      searchFiltersParams.children ||
      searchFiltersParams.adults
        ? {
            babies: searchFiltersParams.babies ?? DEFAULT_PAX.babies,
            children: searchFiltersParams.children ?? DEFAULT_PAX.children,
            adults: searchFiltersParams.adults ?? DEFAULT_PAX.adults,
          }
        : undefined;

    const distances = searchFiltersParams.distances
      ? searchFiltersParams.distances.map(
          (distance) => `${distance.start ?? ''}-${distance.end ?? ''}`
        )
      : undefined;

    const prices = searchFiltersParams.prices
      ? searchFiltersParams.prices.map(
          (price) => `${price.start ?? ''}-${price.end ?? ''}`
        )
      : undefined;

    const accessibility =
      searchFiltersParams.hotelServices?.map((service) => `${service}`) ?? [];

    if (searchFiltersParams.pmr) {
      accessibility.push('pmr');
    }

    return {
      dates: searchFiltersParams.dates,
      distances,
      prices,
      accessibility: accessibility.length ? accessibility : undefined,
      sort: searchFiltersParams.sort,
      nightCount: searchFiltersParams.nightCount,
      pax,
      experienceType: isDefined(searchFiltersParams.nightCount)
        ? experienceType
        : undefined,
      coords: searchFiltersParams.coords,
      experience: selectedCriteriaTagIds(
        tags,
        'experience',
        searchFiltersParams.tagIds
      ),
      room: selectedCriteriaTagIds(tags, 'room', searchFiltersParams.tagIds),
      situation: selectedCriteriaTagIds(
        tags,
        'situation',
        searchFiltersParams.tagIds
      ),
      style: selectedCriteriaTagIds(tags, 'style', searchFiltersParams.tagIds),
    };
  }
};

const formatEditoEventSectionResponse = (
  section: IEditoEventSectionResponse,
  tags: Tag[]
) => {
  return {
    id: section.id,
    pictures: section.pictures ?? [],
    sectionType: section.sectionType,
    title: section.title,
    description: section.description,
    actionType: section.actionType ?? 'empty',
    packageId: section.packageId,
    hotelId: section.hotelId,
    daytimePackValue: buildDaytimePackValueFromSection(section),
    label: section.label,
    webLink: section.webLink,
    appLink: section.appLink,
    searchFilters:
      section.actionType === 'search' && section.webLink
        ? buildSearchFiltersFromWebLink(section.webLink, tags)
        : undefined,
    priority: section.priority,
  };
};

export const formatEditoEventResponseToForm = (
  editoEvent: IEditoEventResponse,
  tags: Tag[]
): IEditoEventForm => {
  const footerSection = editoEvent.sections.find(
    (section): section is IEditoEventSectionFooter =>
      section.sectionType === 'footer'
  );

  return {
    id: editoEvent.id,
    countryId: editoEvent.countryId,
    published: editoEvent.published,
    title: editoEvent.title,
    catchphrase: editoEvent.catchphrase,
    clubId: editoEvent.clubId ? editoEvent.clubId : 'empty',
    picture: editoEvent.picture,
    description: editoEvent.description,
    endDate: editoEvent.endDate ? new Date(editoEvent.endDate) : undefined,
    publishedAt: editoEvent.publishedAt,
    startDate: editoEvent.startDate
      ? new Date(editoEvent.startDate)
      : undefined,
    sections: editoEvent.sections
      .filter((section) => section.sectionType === 'body')
      .map(
        (section) =>
          formatEditoEventSectionResponse(
            section,
            tags
          ) as IEditoEventSectionBody
      ),
    endingBlock: footerSection
      ? (formatEditoEventSectionResponse(
          footerSection,
          tags
        ) as IEditoEventSectionFooter)
      : emptyEndingBlock,
    slug: editoEvent.slug,
  };
};

const getCoords = (
  placeId: string | undefined
): Promise<{ lat: number; lng: number } | undefined> => {
  return new Promise((resolve) => {
    if (!placeId) {
      return resolve(undefined);
    }

    placesService.getDetails(
      {
        placeId,
        fields: ['geometry', 'name'],
      },
      (placeResult: PlaceResult) => {
        if (placeResult && placeResult.geometry?.location) {
          const coords = {
            lat: placeResult.geometry.location.lat(),
            lng: placeResult.geometry.location.lng(),
          };

          resolve(coords);
        }
      }
    );
  });
};

const formatSection = async (
  section: IEditoEventSectionForm
): Promise<IEditoEventSectionRequest> => {
  const pictureIds = section.pictures?.filter((id) => !!id).map((p) => p?.id);

  const coords = await getCoords(section.searchFilters?.placeId);
  const searchFilters = { coords, ...section.searchFilters };

  const daytimePack =
    section.actionType === 'pack'
      ? parsePackSelectionValue(section.daytimePackValue)
      : {};

  const formatEditoEventBase = {
    id: section.id,
    title: section.title,
    description: section.description,
    label: section.label,
    webLink:
      section.actionType === 'search' && searchFilters
        ? buildWebLinkFromSearchFilters(searchFilters)
        : section.webLink,
    appLink:
      section.actionType === 'search' && searchFilters
        ? buildAppLinkFromSearchFilters(searchFilters)
        : section.appLink,
    packageId:
      section.actionType === 'package'
        ? (section.packageId ?? undefined)
        : daytimePack.packageId,
    hotelId: section.actionType === 'hotel' ? section.hotelId : undefined,
    packId: daytimePack.packId,
    priority: section.priority,
  };

  if (section.sectionType === 'body') {
    return {
      ...formatEditoEventBase,
      pictureIds: pictureIds ?? [],
      sectionType: 'body',
      actionType:
        section.actionType === 'empty'
          ? undefined
          : (section.actionType as EditoActionType),
    };
  }

  return {
    ...formatEditoEventBase,
    priority: 0,
    sectionType: 'footer',
    actionType:
      section.actionType === 'empty'
        ? undefined
        : (section.actionType as EditoActionFooterType),
  };
};

export const formatEditoEventFormToRequest = async (
  editoEvent: IEditoEventForm
): Promise<IEditoEventRequest> => {
  const sections = await Promise.all([
    ...editoEvent.sections.map(formatSection),
    formatSection(editoEvent.endingBlock),
  ]);

  return {
    id: editoEvent.id,
    countryId: editoEvent.countryId,
    published: editoEvent.published,
    sections,
    title: editoEvent.title.trim(),
    catchphrase: editoEvent.catchphrase?.trim(),
    clubId: editoEvent.clubId === 'empty' ? undefined : editoEvent.clubId,
    pictureId: editoEvent.picture?.id,
    description: editoEvent.description?.trim(),
    endDate: editoEvent.endDate
      ? formatToLocalDateTime(editoEvent.endDate)
      : undefined,
    publishedAt: editoEvent.publishedAt,
    startDate: editoEvent.startDate
      ? formatToLocalDateTime(editoEvent.startDate)
      : undefined,
  };
};
